/* Copyright (c) 2020-present CloudIO Inc */

import { useStateXSelector } from '@cloudio-saas/statex';
import { Slider, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { NumberProp } from 'componentregistry';
import { helpTextSelector } from 'designer/state';
import Icon from 'icons/Icon';
import React, { memo, useId, useState } from 'react';
import useLocalState from 'hooks/useLocalState';

import { getDefaultValues } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
  },
  inner: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    backgroundColor: theme.palette.background.default,
    borderRadius: 4,
    position: 'relative',
  },
  label: {
    flex: 0,
    minWidth: 80,
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  option: {
    transition: 'color 0.2s',
    border: '1px solid transparent',
    color: theme.palette.text.secondary,
    alignSelf: 'center',
    fontSize: '0.8rem',
    padding: '4px 0',
    position: 'relative',
    flex: 1,
    cursor: 'pointer',
    textAlign: 'center',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    '& > svg': {
      height: '1em',
      width: '1em',
    },
    '&:not(:last-child)::after': {
      content: '""',
      position: 'absolute',
      top: 8,
      bottom: 8,
      right: 0,
      width: 1,
      backgroundColor: theme.palette.divider,
    },
    '&:not(:first-of-type)': {
      marginLeft: '-1px',
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
    },
  },
  slider: {
    flex: 2,
    height: '30px',
    overflow: 'visible',
    padding: '0 16px',
    zIndex: 1,
  },
  input: {
    border: `4px solid ${theme.palette.background.default}`,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    lineHeight: '22px',
    padding: 0,
    textAlign: 'end',
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: '60px',
    width: '60px',
  },
  tip: {
    marginLeft: 8,
  },
}));

function NumberSliderProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<number>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<NumberProp, number>) {
  const classes = useStyles();
  const [undoable, setUndoable] = useState(true);
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });

  if (value === Number.MIN_VALUE || value === Number.MAX_VALUE) {
    value = 0;
  }

  const [localValue, setLocalValue] = useLocalState<number | undefined>(
    value,
    (val) => {
      onChange(property, val, undoable);
      setUndoable(false);
    },
  );
  const id = useId();

  return (
    <div data-testid={property} className={classes.root}>
      <label htmlFor={id} className={classes.label}>
        {def.label}
      </label>
      <div data-testid="inner" className={classes.inner}>
        <div className={clsx(classes.option, classes.slider)}>
          <Slider
            disabled={isViewOnlyUser}
            valueLabelDisplay="auto"
            step={def.step ?? 1}
            min={def.min ?? 0}
            max={def.max ?? 100}
            onChange={(_: Event, val: number | number[]) => {
              if (typeof val === 'number') {
                setLocalValue(val);
              }
            }}
            value={localValue ?? 0}
          />
        </div>
        {!def.hideInput ? (
          <input
            id={id}
            disabled={isViewOnlyUser}
            data-testid="input"
            type="number"
            className={clsx(classes.option, classes.input)}
            value={localValue ?? ''}
            onChange={(e) => {
              setLocalValue(
                e.target.value ? Number(e.target.value) : undefined,
              );
            }}
            min={def.min ?? 0}
            max={def.max ?? 100}
          />
        ) : null}
      </div>
      {helpText ? (
        <Icon
          className={classes.tip}
          data-tip-at="top"
          data-tip-delay={0}
          data-tip={helpText}
          iconName="comment-exclamation"
        />
      ) : null}
    </div>
  );
}

export default memo(NumberSliderProperty);
